/**
 * Scene map (SPEC §5.3): which layout file drives which camera scene, and which
 * camera each layout slot shows. Part of the graphics app's project config.
 *
 * ```
 * MAIN                  → layoutBySpeakerCount { 2: "2-box.json", … 6: "6-box.json" }
 * Host Single           → single-box.json (slot "main" ← CAM-1)
 * Speaker n Single      → single-box.json (slot "main" ← CAM-(n+1))
 * Speaker + Screenshare → speaker-share.json (slot "speaker" ← CAM-n by dropdown, slot "share" ← SHARE)
 * ```
 */
import { z } from 'zod';
import { CameraIdSchema, SINGLE_SCENE_CAMERA, type SingleSceneKey } from './fixture.ts';
import { MAX_PARTICIPANTS, MIN_MAIN_PARTICIPANTS } from './session.ts';
import { findDuplicates, parseOrThrow } from './validate.ts';

export const SCENE_MAP_SCHEMA_VERSION = 1;

/** A layout file name, relative to the project's layouts directory. No paths. */
const layoutFileName = z
  .string()
  .regex(/^[a-z0-9]+(-[a-z0-9]+)*\.json$/, 'must be a kebab-case .json file name, e.g. "4-box.json"');

const slotId = z.string().regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'must be a kebab-case slot ID');

/** Every participant count `MAIN` must cover: 2 … 6. */
export const MAIN_SPEAKER_COUNTS = Array.from(
  { length: MAX_PARTICIPANTS - MIN_MAIN_PARTICIPANTS + 1 },
  (_, i) => String(i + MIN_MAIN_PARTICIPANTS),
) as readonly string[];

const singleEntry = (key: SingleSceneKey) =>
  z.strictObject({
    layout: layoutFileName,
    /** The layout slot that shows this scene's camera. */
    slot: slotId,
    /** Must be the fixture's camera for this scene; stated here so the file reads on its own. */
    camera: z.literal(SINGLE_SCENE_CAMERA[key]),
  });

export const SceneMapSchema = z.strictObject({
  schemaVersion: z.literal(SCENE_MAP_SCHEMA_VERSION),
  main: z
    .strictObject({
      /** Keyed by the session's participant count from the sheet (host included), 2 … 6. */
      layoutBySpeakerCount: z.record(z.string(), layoutFileName),
      /** Which camera each MAIN layout slot shows, e.g. `{ "host": "CAM-1", "guest-1": "CAM-2" }`. */
      slotCameras: z.record(slotId, CameraIdSchema),
    })
    .superRefine((main, ctx) => {
      const counts = Object.keys(main.layoutBySpeakerCount);
      for (const n of MAIN_SPEAKER_COUNTS) {
        if (!counts.includes(n)) {
          ctx.addIssue({ code: 'custom', path: ['layoutBySpeakerCount'], message: `missing a layout for ${n} participants` });
        }
      }
      for (const n of counts) {
        if (!MAIN_SPEAKER_COUNTS.includes(n)) {
          ctx.addIssue({
            code: 'custom',
            path: ['layoutBySpeakerCount', n],
            message: `participant count must be ${MAIN_SPEAKER_COUNTS.join(', ')}`,
          });
        }
      }
      for (const dup of findDuplicates(Object.values(main.slotCameras))) {
        ctx.addIssue({ code: 'custom', path: ['slotCameras'], message: `camera ${dup} is mapped to more than one slot` });
      }
    }),
  hostSingle: singleEntry('hostSingle'),
  speaker1Single: singleEntry('speaker1Single'),
  speaker2Single: singleEntry('speaker2Single'),
  speaker3Single: singleEntry('speaker3Single'),
  speaker4Single: singleEntry('speaker4Single'),
  speaker5Single: singleEntry('speaker5Single'),
  speakerShare: z
    .strictObject({
      layout: layoutFileName,
      /** Every CAM-n item is stacked here; the control app's dropdown picks the one enabled. */
      speakerSlot: slotId,
      /** Filled by the SHARE nested scene. */
      shareSlot: slotId,
    })
    .refine((s) => s.speakerSlot !== s.shareSlot, { message: 'speakerSlot and shareSlot must differ' }),
});
export type SceneMap = z.infer<typeof SceneMapSchema>;

/** Validates a parsed scene map. @throws ValidationError listing every problem. */
export function parseSceneMap(data: unknown, fileName = 'scene-map.json'): SceneMap {
  return parseOrThrow(SceneMapSchema, data, `scene map "${fileName}"`);
}

/**
 * The MAIN layout for a session (SPEC §5.4): chosen by the sheet's participant count,
 * host included. The live roster only cross-checks it, elsewhere.
 * @throws RangeError outside 2…6: never guess a layout.
 */
export function mainLayoutFor(map: SceneMap, participantCount: number): string {
  const layout = Number.isInteger(participantCount) ? map.main.layoutBySpeakerCount[String(participantCount)] : undefined;
  if (layout === undefined) {
    throw new RangeError(`no MAIN layout for ${participantCount} participants (supported: ${MAIN_SPEAKER_COUNTS.join(', ')})`);
  }
  return layout;
}

/** Every layout file the scene map references, deduplicated. */
export function referencedLayoutFiles(map: SceneMap): string[] {
  return [
    ...new Set([
      ...Object.values(map.main.layoutBySpeakerCount),
      map.hostSingle.layout,
      map.speaker1Single.layout,
      map.speaker2Single.layout,
      map.speaker3Single.layout,
      map.speaker4Single.layout,
      map.speaker5Single.layout,
      map.speakerShare.layout,
    ]),
  ];
}
